import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { AddressFactory } from '../../../address';
import { handleFormFieldValidation } from '../../../services/form-validation-service';
import { countryFactory } from '../../../address/countries';
import style from './new-order-modal.module.less';
import messages from './messages';
import { FIELD_NAMES } from '../../../address/address-fields/constants';
import { getParsedAddress } from '../../../address/address.api';
import { ModalService } from 'ui-components/services/modal-service';
import { ErrorModal } from '../../../components/modals/error-modal';
import { useOnComponentMount } from '../../../hooks/use-on-component-mount';
import Global from '../../../store/global-selectors';
import * as CustomerActions from '../../../store/customers/autocomplete/customers.autocomplete.actions';
import { logPasteAddressUsage } from '../../../address/actions';
import { EditOrderRequestPropType } from '../../../prop-types/edit-order-request';

const makeRequiredFieldsObject = (countryCode) => {
  return Object.fromEntries(
    countryFactory(countryCode)
      .addressFormat.elements.filter((f) => f.required)
      .map((a) => [a.name, a.name]),
  );
};

export const RecipientInformation = ({ order, setOrder, fieldsWithError, setFieldsWithError }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const autoCompleteCustomers = useSelector(Global.selectCustomersAutocompleteSearchResults);
  const clearCustomerResults = useCallback(() => dispatch(CustomerActions.clearCustomerSearch()), [dispatch]);
  const onCustomerSearchChange = useCallback(
    (searchTerm) => {
      dispatch(CustomerActions.onCustomerSearchChange(searchTerm));
    },
    [dispatch],
  );

  const { shipToAddress } = order;
  const [pasteAddressValue, setPasteAddressValue] = useState('');
  const [mounted, setMounted] = useState(false);
  useOnComponentMount(() => {
    setMounted(true);
    return () => setMounted(false);
  });

  const validateFormValues = useCallback(
    (name, value) => {
      // Only validate required fields

      const requiredFields = makeRequiredFieldsObject(order?.shipToAddress?.countryCode);

      if (requiredFields[name] === name) {
        const error = handleFormFieldValidation(name, value);
        const newFieldsWithError = {
          ...fieldsWithError,
          [name]: error,
        };
        setFieldsWithError(newFieldsWithError);
      }
      // Close name search results overlay onBlur of name field only
      if (name === requiredFields.name) {
        setTimeout(() => {
          clearCustomerResults();
        }, 200);
      }
    },
    [order, fieldsWithError, setFieldsWithError, clearCustomerResults],
  );

  const onAddressChange = useCallback(
    (field, value) => {
      if (field === FIELD_NAMES.NAME && order?.shipToAddress?.name !== value) {
        onCustomerSearchChange(value);
      }

      if (field === FIELD_NAMES.COUNTRY_CODE) {
        setFieldsWithError({});
      }

      setOrder({
        ...order,
        shipToAddress: {
          ...order.shipToAddress,
          [field]: value,
        },
      });
    },
    [order, setOrder, onCustomerSearchChange, setFieldsWithError],
  );

  const onCustomerSelected = useCallback(
    (customerIndex) => {
      const selectedCustomer = autoCompleteCustomers[customerIndex];
      if (selectedCustomer) {
        setOrder({
          ...order,
          shipToAddress: {
            ...order.shipToAddress,
            name: selectedCustomer.name,
            company: selectedCustomer.company,
            line1: selectedCustomer.line1,
            line2: selectedCustomer.line2,
            line3: selectedCustomer.line3,
            city: selectedCustomer.city,
            state: selectedCustomer.state,
            postalCode: selectedCustomer.postalCode,
            countryCode: selectedCustomer.countryCode,
            phone: selectedCustomer.phone,
            email: selectedCustomer.email,
          },
        });
        clearCustomerResults();
      }
    },
    [order, setOrder, clearCustomerResults, autoCompleteCustomers],
  );

  const pasteAddressRequest = useCallback(
    (newValue) => {
      setPasteAddressValue(newValue);
      if (typeof newValue === 'string' && newValue.trim().length > 0) {
        getParsedAddress(newValue).subscribe(
          (response) => {
            dispatch(logPasteAddressUsage());
            if (mounted) {
              setOrder({
                ...order,
                shipToAddress: {
                  ...order.shipToAddress,
                  ...response,
                  phone: response.phone ?? order.shipToAddress.phone,
                },
              });
            }
          },
          () => {
            ModalService.launchModal({
              component: ErrorModal,
              props: {
                title: intl.formatMessage(messages.parseFailTitle),
                message: intl.formatMessage(messages.parseFailMessage),
              },
            });
          },
        );
      }
    },
    [order, setOrder, setPasteAddressValue, intl, mounted, dispatch],
  );

  return (
    <div className={style['recipient-information-container']}>
      <div className={style['section-title']}>
        <FormattedMessage {...messages.recipientTitle} />
      </div>
      <AddressFactory
        address={shipToAddress}
        autoCompleteRecipientName
        customers={autoCompleteCustomers}
        hasError={fieldsWithError}
        isControlled
        onBlur={validateFormValues}
        onChange={onAddressChange}
        onCustomerSelected={onCustomerSelected}
        pasteAddressRequest={pasteAddressRequest}
        pasteAddressValue={pasteAddressValue}
      />
    </div>
  );
};

RecipientInformation.propTypes = {
  order: EditOrderRequestPropType.isRequired,
  setOrder: PropTypes.func.isRequired,
  fieldsWithError: PropTypes.object.isRequired,
  setFieldsWithError: PropTypes.func.isRequired,
};
